import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Tabs } from 'antd'

import { BasicTable, NewTable, PageTopBar, PageView, Notification } from '../../components'
import { ButtonLoader } from '../../components'
import { Loader } from '../../components/Loader'
import { SlidePanel } from '../../components/SlidePanel'
import { setCycleCountsPageSize } from '../../redux/pageSizeSlice'
import { fetchCycleCounts, fetchCycleCountReviews } from '../../services/cycleCountServices'
import { fetchWarehouseLocations } from '../../services/locationServices'
import { CoverageEventsTab } from './events/CoverageEventsTab'
import { coverageEventQueryKeys } from './events/coverageEventQueryKeys'
import { collectUnassignedIds } from './events/collectUnassignedIds'
import { AssignCycleCountDialog } from './AssignCycleCountDialog'
import { CycleCountDetailPanel } from './CycleCountDetailPanel'
import { BulkAssignCycleCountDialog } from './BulkAssignCycleCountDialog'
import { UnassignCycleCountDialog } from './UnassignCycleCountDialog'
import { ReassignCycleCountDialog } from './ReassignCycleCountDialog'
import { ResolveReviewDialog } from './ResolveReviewDialog'
import { flattenReviewGroups, selectionStatus, toBatchesForReassign, truncatedGroups } from './reviewRows'
import { parseCoverageEventDeepLink } from './events/coverageEventLinks'
import { foundStockDeepLink, reasonLabel, summarizeResolveFoundStock } from '../FoundStock/foundStockLinks'
import { foundStockQueryKeys } from '../FoundStock/foundStockQueryKeys'
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime'
import { SelectColumnFilter } from '../../components/BasicTable/modules/ColumnFiltersUI'
import { LOCATION_TYPES } from '../Locations/LocationTypes'
import { PlusIcon } from '@heroicons/react/outline'
import { getSkus } from '../../utils/productUtils'

const T_PREFIX = 'cycle_counts'
const EVENTS_T_PREFIX = 'cycle_counts.events'

export const ListCycleCountsContainer = () => {
  const { i18n } = useTranslation()
  const { warehouse_id } = useParams()
  const dispatch = useDispatch()
  const pageSize = useSelector(state => state.pageSize.cycleCountsList)

  const { TabPane } = Tabs

  // State
  const { search } = useLocation()
  // A deep link (`?event_id=`, from Sobrantes naming the event that froze a declaration) lands on
  // the Eventos tab with that event open. Read once at mount, like Sobrantes reads its own params.
  const [linkedEventId] = useState(() => parseCoverageEventDeepLink(search))
  const [activeTab, setActiveTab] = useState(linkedEventId != null ? 'events' : 'locations')
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false)
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false)
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false)
  const [selectedLocations, setSelectedLocations] = useState([])
  const [selectedBatches, setSelectedBatches] = useState([])
  const [deselectBatches, setDeselectBatches] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [taskPanelOpen, setTaskPanelOpen] = useState(false)
  // Set by the Conteos row's "Evento #id" link, or by the deep link above: switches to the Eventos
  // tab and tells it which event to open, without lifting the tab's own detail-panel state up here.
  const [eventsOpenId, setEventsOpenId] = useState(linkedEventId)
  const [notification, setNotification] = useState({ show: false, type: '', title: '' })
  const tableRef = useRef()

  // Locations tab state
  const [locationParams, setLocationParams] = useState({
    page: 1,
    per_page: pageSize,
    countable_only: true,
  })

  // Conteos tab state
  const [conteosParams, setConteosParams] = useState({
    page: 1,
    per_page: pageSize,
    warehouse_id: warehouse_id,
  })

  // Reviews tab state
  const [reviewsParams, setReviewsParams] = useState({ page: 1, per_page: pageSize, warehouse_id })
  const [selectedReviewRows, setSelectedReviewRows] = useState([])
  const [deselectReviews, setDeselectReviews] = useState(false)
  const [reviewReassignOpen, setReviewReassignOpen] = useState(false)
  const [resolveOpen, setResolveOpen] = useState(false)
  const [reviewUnassignOpen, setReviewUnassignOpen] = useState(false)
  const [resolvedSurplus, setResolvedSurplus] = useState(null)

  const queryClient = useQueryClient()

  // Only fetch locations when at least one filter is set (same pattern as inventory page)
  const locationsQueryEnabled = useMemo(() => {
    const filterKeys = Object.keys(locationParams).filter(
      key => locationParams[key] != null && locationParams[key] !== '' && key !== 'page' && key !== 'per_page' && key !== 'countable_only',
    )
    const enabled = filterKeys.length > 0
    if (!enabled) queryClient.resetQueries('cycle_count_locations_list', { exact: false })
    return enabled
  }, [locationParams, queryClient])

  // Locations query
  const {
    isLoading: locationsLoading,
    data: locationsData,
    isFetching: locationsFetching,
    isPreviousData: locationsPreviousData,
    refetch: refetchLocations,
  } = useQuery(['cycle_count_locations_list', warehouse_id, locationParams], () => fetchWarehouseLocations(warehouse_id, locationParams), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: locationsQueryEnabled,
  })

  // Conteos query
  const {
    isLoading: conteosLoading,
    data: conteosData,
    isFetching: conteosFetching,
    isPreviousData: conteosPreviousData,
    isError: conteosError,
    refetch: refetchConteos,
  } = useQuery(['cycle_counts_list', conteosParams], () => fetchCycleCounts(conteosParams), { keepPreviousData: true, refetchOnWindowFocus: false })

  // Reviews query
  const {
    isLoading: reviewsLoading,
    data: reviewsData,
    isFetching: reviewsFetching,
    isPreviousData: reviewsPreviousData,
    isError: reviewsError,
    refetch: refetchReviews,
  } = useQuery(['cycle_count_reviews', reviewsParams], () => fetchCycleCountReviews(reviewsParams), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: activeTab === 'reviews',
  })

  // Notification helper
  const showNotification = (type, title) => {
    setNotification({ show: true, type, title })
  }

  // A refetch can drop a selected batch or flip its state past selectability
  // (NewTable re-selects by id without re-checking _disabled), leaving the
  // destructive buttons armed against rows the supervisor can no longer untick.
  useEffect(() => {
    if (selectedBatches.length === 0) return
    const selectableIds = new Set(
      (conteosData?.data || []).filter(t => t.status === 'pending' || t.status === 'review_pending' || t.status === 'unassigned').map(t => t.id),
    )
    if (!selectedBatches.every(b => selectableIds.has(b.id))) clearBatchSelection()
  }, [conteosData, selectedBatches]) // eslint-disable-line react-hooks/exhaustive-deps

  // Assignment success handler
  const onAssignSuccess = result => {
    setAssignDialogOpen(false)
    tableRef.current?.resetSelection()
    setSelectedLocations([])
    refetchLocations()
    refetchConteos()
    refetchReviews()
    const takenOver = result?.taken_over_count ?? 0
    showNotification(
      'success',
      takenOver > 0
        ? i18n.t(`${T_PREFIX}.assign_modal.success_with_take_over`, { count: result?.created_count ?? selectedLocations.length, taken_over: takenOver })
        : i18n.t(`${T_PREFIX}.assign_modal.success`),
    )
  }

  const clearBatchSelection = () => {
    setSelectedBatches([])
    setDeselectBatches(true)
  }

  const onUnassignSuccess = result => {
    setUnassignDialogOpen(false)
    clearBatchSelection()
    refetchLocations()
    refetchConteos()
    refetchReviews()
    // An unassigned coverage-event count reopens that location's coverage; the Eventos tab's
    // list/detail queries would otherwise show stale counts until an unrelated refetch.
    queryClient.invalidateQueries(coverageEventQueryKeys.all)
    // A chunked pool unassignment that failed partway still unassigned what it could — see
    // the same reasoning on onReassignSuccess below.
    if (result?.partial) {
      showNotification('warning', result.message)
      return
    }
    showNotification(
      'success',
      i18n.t(`${T_PREFIX}.unassign_modal.success`, {
        count: result?.unassigned_count ?? 0,
      }),
    )
  }

  const onReassignSuccess = result => {
    setReassignDialogOpen(false)
    clearBatchSelection()
    refetchLocations()
    refetchConteos()
    refetchReviews()
    queryClient.invalidateQueries(coverageEventQueryKeys.all)
    // A chunked pool assignment that failed partway still assigned what it could — the
    // lists must refetch either way, but a green "success" toast would hide the failure.
    if (result?.partial) {
      showNotification('warning', result.message)
      return
    }
    showNotification(
      'success',
      i18n.t(`${T_PREFIX}.reassign_modal.success`, {
        count: result?.reassigned_count ?? 0,
      }),
    )
  }

  const onBulkAssignSuccess = result => {
    setBulkAssignOpen(false)
    refetchLocations()
    refetchConteos()
    // Bulk-assigned counts can be ones an open coverage event is watching.
    queryClient.invalidateQueries(coverageEventQueryKeys.all)
    const takenOver = result?.taken_over_count ?? 0
    if (result?.distribution) {
      const details = result.distribution.map(d => `${d.name} (${d.count})`).join(', ')
      const skipped = result?.skipped_count ?? 0
      // Multi-operator assign can also skip or take over locations — report those counts
      // instead of only the per-operator breakdown, mirroring the single-operator path below.
      if (skipped > 0 || takenOver > 0) {
        showNotification(
          'success',
          i18n.t(`${T_PREFIX}.bulk_assign.success_multi_with_skipped`, {
            count: result.created_count,
            details,
            skipped,
            taken_over: takenOver,
          }),
        )
      } else {
        showNotification(
          'success',
          i18n.t(`${T_PREFIX}.bulk_assign.success_multi`, {
            count: result.created_count,
            details,
          }),
        )
      }
    } else if (takenOver > 0 && result?.skipped_count > 0) {
      // Both can be positive at once (some locations took over a pending count, others were
      // skipped outright) — report all three counts instead of hiding the skipped ones.
      showNotification(
        'success',
        i18n.t(`${T_PREFIX}.bulk_assign.success_with_take_over_and_skipped`, {
          created: result.created_count,
          taken_over: takenOver,
          skipped: result.skipped_count,
        }),
      )
    } else if (takenOver > 0) {
      showNotification(
        'success',
        i18n.t(`${T_PREFIX}.assign_modal.success_with_take_over`, {
          count: result?.created_count || 0,
          taken_over: takenOver,
        }),
      )
    } else if (result?.skipped_count > 0) {
      showNotification(
        'success',
        i18n.t(`${T_PREFIX}.bulk_assign.success_with_skipped`, {
          created: result.created_count,
          skipped: result.skipped_count,
        }),
      )
    } else {
      showNotification(
        'success',
        i18n.t(`${T_PREFIX}.bulk_assign.success`, {
          count: result?.created_count || 0,
        }),
      )
    }
  }

  // The server caps a batch row's `locations` at 100 and flags the ones it trimmed;
  // `total_locations` still carries the true count so the notice can point at what's hidden.
  // The label lets more than one truncated batch show up at once without the notices
  // reading as duplicates of each other.
  const conteosTruncatedBatches = useMemo(
    () =>
      (conteosData?.data || [])
        .filter(batch => batch.locations_truncated)
        .map(batch => ({
          id: batch.id,
          shown: (batch.locations || []).length,
          total: batch.total_locations,
          label: batch.id === 'batch_unassigned' ? i18n.t(`${T_PREFIX}.conteos.status_unassigned`) : batch.operator?.full_name || `#${batch.id}`,
        })),
    [conteosData, i18n],
  )

  // The unassigned pool row only ever carries its first 100 locations — reassigning it must
  // walk the whole paged pool instead of the capped list the row happens to show. This only
  // applies to a selection of that row ALONE: mixed with any other batch, the pool walk would
  // silently replace the other batches' ids with the pool's, dropping them from the request.
  const unassignedBatchSelected = selectedBatches.length === 1 && selectedBatches[0].id === 'batch_unassigned'
  // The plain (non-pool) path collects each selected batch's own `.locations` — capped at 100
  // for the pool row — so mixing the pool with any other batch would silently act on only the
  // pool's first 100 rows while dropping neither batch from the request. Block the combination
  // outright rather than let it half-work.
  const mixedPoolSelection = selectedBatches.length > 1 && selectedBatches.some(b => b.id === 'batch_unassigned')
  const resolveUnassignedPoolIds = useCallback(
    () => collectUnassignedIds({ warehouseId: warehouse_id, origin: conteosParams.origin }),
    [warehouse_id, conteosParams.origin],
  )

  // Reviews derived state
  const reviewRows = useMemo(() => flattenReviewGroups(reviewsData?.data), [reviewsData])
  const reviewTruncatedGroups = useMemo(() => truncatedGroups(reviewsData?.data), [reviewsData])
  const reviewSelectionStatus = useMemo(() => selectionStatus(selectedReviewRows), [selectedReviewRows])
  const canResolve = selectedReviewRows.length === 1 && reviewSelectionStatus === 'review_submitted'
  const canAssignReview = selectedReviewRows.length > 0 && reviewSelectionStatus !== 'mixed'
  // Discard deletes the count (server allows submitted/review_pending, validated per row); a
  // reviewed disagreement is closed through Resolver instead. Unlike Asignar/Reasignar and
  // Resolver, this one doesn't require a homogeneous selection — a manager can discard an
  // unassigned review together with an already-assigned one in a single action.
  const canDiscardReview = selectedReviewRows.length > 0 && selectedReviewRows.every(r => ['submitted', 'review_pending'].includes(r.status))

  const clearReviewSelection = () => {
    setSelectedReviewRows([])
    setDeselectReviews(true)
  }

  // Same guard as the Conteos tab: a refetch must not leave buttons armed on vanished rows.
  useEffect(() => {
    if (selectedReviewRows.length === 0) return
    const ids = new Set(reviewRows.map(r => r.id))
    if (!selectedReviewRows.every(r => ids.has(r.id))) clearReviewSelection()
  }, [reviewRows, selectedReviewRows]) // eslint-disable-line react-hooks/exhaustive-deps

  const onReviewReassignSuccess = result => {
    setReviewReassignOpen(false)
    clearReviewSelection()
    refetchReviews()
    refetchConteos()
    queryClient.invalidateQueries(coverageEventQueryKeys.all)
    // A review selection can exceed the server's 500-id cap too (select-all across many
    // groups) and the dialog chunks it the same way as the Conteos tab — a partial failure
    // must not read as a green success.
    if (result?.partial) {
      showNotification('warning', result.message)
      return
    }
    showNotification('success', i18n.t(`${T_PREFIX}.reassign_modal.success`, { count: result?.reassigned_count ?? 0 }))
  }

  const onResolveSuccess = result => {
    const location = selectedReviewRows[0]?._location
    const summary = summarizeResolveFoundStock(result)
    setResolvedSurplus(
      summary && (summary.unitsCreated > 0 || summary.rowsSkipped > 0)
        ? { ...summary, location_code: location?.location_code, cycle_count_id: location?.id }
        : null,
    )
    setResolveOpen(false)
    clearReviewSelection()
    refetchReviews()
    refetchConteos()
    // Surplus declared through resolve doesn't otherwise touch the queue query — nudge the hub badge.
    queryClient.invalidateQueries(foundStockQueryKeys.queueTotal(warehouse_id))
    queryClient.invalidateQueries(coverageEventQueryKeys.all)
    showNotification('success', i18n.t(`${T_PREFIX}.resolve_modal.success`))
  }

  const onReviewUnassignSuccess = result => {
    setReviewUnassignOpen(false)
    clearReviewSelection()
    refetchReviews()
    refetchConteos()
    queryClient.invalidateQueries(coverageEventQueryKeys.all)
    // Same reasoning as onReviewReassignSuccess: the discard dialog chunks a selection over
    // the server's 500-id cap too, and a partial failure must not read as a green success.
    if (result?.partial) {
      showNotification('warning', result.message)
      return
    }
    showNotification('success', i18n.t(`${T_PREFIX}.unassign_modal.success`, { count: result?.unassigned_count ?? 0 }))
  }

  const getReviewStatusBadge = useCallback(
    status => {
      const map = {
        submitted: ['bg-red-100 text-red-800', 'status_unassigned'],
        review_pending: ['bg-orange-100 text-orange-800', 'status_pending'],
        review_submitted: ['bg-blue-100 text-blue-800', 'status_disagreement'],
      }
      const [color, key] = map[status] || map.submitted
      return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{i18n.t(`${T_PREFIX}.reviews.${key}`)}</span>
    },
    [i18n],
  )

  // Get status badge
  const getStatusBadge = useCallback(
    status => {
      const colors = {
        unassigned: 'bg-red-100 text-red-800',
        pending: 'bg-yellow-100 text-yellow-800',
        in_progress: 'bg-blue-100 text-blue-800',
        completed: 'bg-green-100 text-green-800',
        review_pending: 'bg-orange-100 text-orange-800',
        review_submitted: 'bg-blue-100 text-blue-800',
        resolved: 'bg-green-100 text-green-800',
        in_review: 'bg-amber-100 text-amber-800',
      }
      const labels = {
        unassigned: i18n.t(`${T_PREFIX}.conteos.status_unassigned`),
        pending: i18n.t(`${T_PREFIX}.conteos.status_pending`),
        in_progress: i18n.t(`${T_PREFIX}.conteos.status_in_progress`),
        completed: i18n.t(`${T_PREFIX}.conteos.status_completed`),
        review_pending: i18n.t(`${T_PREFIX}.conteos.status_review_pending`),
        review_submitted: i18n.t(`${T_PREFIX}.conteos.status_review_submitted`),
        resolved: i18n.t(`${T_PREFIX}.conteos.status_resolved`),
        in_review: i18n.t(`${T_PREFIX}.conteos.status_in_review`),
      }
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
          {labels[status] || status}
        </span>
      )
    },
    [i18n],
  )

  // Get origin badge — origins is the distinct set of location origins in the batch.
  // Absent before the origins field shipped on the API, so render '-' rather than crash.
  const getOriginBadge = useCallback(
    origins => {
      if (!origins || origins.length === 0) return '-'
      if (origins.length > 1) {
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
            {i18n.t(`${T_PREFIX}.conteos.origin_mixed`)}
          </span>
        )
      }
      const labels = {
        manual: i18n.t(`${T_PREFIX}.conteos.origin_manual`),
        missing_stock: i18n.t(`${T_PREFIX}.conteos.origin_missing_stock`),
        coverage_event: i18n.t(`${T_PREFIX}.conteos.origin_coverage_event`),
      }
      const colors = {
        manual: 'bg-gray-100 text-gray-800',
        missing_stock: 'bg-purple-100 text-purple-800',
        coverage_event: 'bg-blue-100 text-blue-800',
      }
      const key = origins[0]
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[key] || colors.manual}`}>{labels[key] || key}</span>
      )
    },
    [i18n],
  )

  // Render last-counted-at cell
  const getLastCountedAt = useCallback(
    isoDate => {
      if (!isoDate) {
        return <span className="text-gray-400">{i18n.t(`${T_PREFIX}.locations.never_counted`)}</span>
      }
      return <FormattedRelativeTime date={isoDate} className="text-gray-600" />
    },
    [i18n],
  )

  // Get assignment status display for locations
  const getAssignmentStatus = useCallback(
    location => {
      const loc = location.warehouse_location || location
      const isAssigned = !!loc.cycle_count_assigned_to
      if (isAssigned) {
        return <span className="text-gray-400">{i18n.t(`${T_PREFIX}.locations.status_assigned`, { operator: loc.cycle_count_assigned_to })}</span>
      }
      return <span className="text-green-600">{i18n.t(`${T_PREFIX}.locations.status_available`)}</span>
    },
    [i18n],
  )

  // Locations columns (BasicTable format — inline filters below headers)
  const locationColumns = [
    {
      Header: i18n.t(`${T_PREFIX}.locations.location_code`),
      accessor: 'location_code',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t(`${T_PREFIX}.locations.zone`),
      accessor: 'zone',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t(`${T_PREFIX}.locations.location_type`),
      accessor: 'location_type',
      disableFilters: false,
      disableSortBy: true,
      Filter: cell => (
        <SelectColumnFilter
          column={cell.column}
          onSubmit={value => {
            cell.column.setFilter(value)
            const activeFilters = cell.columns.reduce((acc, column) => {
              if (column.id === cell.column.id) acc.push({ id: column.id, value })
              else if (column.filterValue) acc.push({ id: column.id, value: column.filterValue })
              return acc
            }, [])
            onLocationFilterChange(activeFilters)
          }}
        />
      ),
      selectFilterOptions: LOCATION_TYPES.map(locationType => ({
        id: locationType,
        label: locationType,
      })),
    },
    {
      Header: i18n.t(`${T_PREFIX}.locations.store`),
      accessor: 'store',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t(`${T_PREFIX}.locations.sku`),
      accessor: 'sku',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t(`${T_PREFIX}.locations.upc`),
      accessor: 'upc',
      disableFilters: false,
      disableSortBy: true,
    },
    {
      Header: i18n.t(`${T_PREFIX}.locations.assignment_status`),
      accessor: 'assignment_status',
      disableFilters: true,
      disableSortBy: true,
    },
    {
      Header: i18n.t(`${T_PREFIX}.locations.last_counted_at`),
      accessor: 'last_counted_at',
      disableFilters: true,
      disableSortBy: true,
    },
  ]

  // Locations table data (BasicTable — no object wrapper needed)
  const locationTableData = useMemo(() => {
    const locations = locationsData?.locations || locationsData?.data || []
    return locations.map(loc => {
      const location = loc.warehouse_location || loc
      const isAssigned = !!location.cycle_count_assigned_to
      const id = location.id || loc.id
      return {
        id,
        location_code: location.unique_code || location.location_code || '-',
        zone: location.area || location.zone?.name || location.zone || '-',
        location_type: location.location_type || '-',
        store: loc.product?.store?.name || location.store?.name || '-',
        sku: getSkus(loc.product?.product_codes),
        upc: loc.product?.bar_code || '-',
        assignment_status: getAssignmentStatus(loc),
        last_counted_at: getLastCountedAt(location.cycle_count_last_counted_at),
        _disabled: isAssigned,
        _original: { id, ...location },
      }
    })
  }, [locationsData, getAssignmentStatus, getLastCountedAt])

  // Conteos columns
  const conteosColumns = useMemo(
    () => [
      {
        Header: i18n.t(`${T_PREFIX}.conteos.operator`),
        accessor: 'operator_name',
      },
      {
        Header: i18n.t(`${T_PREFIX}.conteos.origin`),
        accessor: 'origin',
      },
      {
        Header: i18n.t(`${T_PREFIX}.conteos.locations_count`),
        accessor: 'locations_count',
      },
      {
        Header: i18n.t(`${T_PREFIX}.conteos.progress`),
        accessor: 'progress',
      },
      {
        Header: i18n.t(`${T_PREFIX}.conteos.status`),
        accessor: 'status',
      },
      {
        Header: i18n.t(`${T_PREFIX}.conteos.assigned_date`),
        accessor: 'assigned_date',
      },
    ],
    [i18n],
  )

  // Conteos table data
  const conteosTableData = useMemo(() => {
    if (!conteosData?.data) return []
    return conteosData.data.map(task => ({
      object: { id: task.id, ...task },
      _disabled: !(task.status === 'pending' || task.status === 'review_pending' || task.status === 'unassigned'),
      operator_name: task.operator?.full_name || task.operator?.email || '-',
      origin: getOriginBadge(task.origins),
      locations_count: `${task.total_locations} ${i18n.t(`${T_PREFIX}.locations.title`)}`,
      progress: (
        <div className="flex items-center">
          <span className="mr-2">
            {task.completed_locations}/{task.total_locations}
          </span>
          <div className="w-24 bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full"
              style={{ width: `${task.total_locations > 0 ? (task.completed_locations / task.total_locations) * 100 : 0}%` }}
            />
          </div>
          {task.pending_reviews > 0 && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              {i18n.t(`${T_PREFIX}.conteos.pending_reviews`, { count: task.pending_reviews })}
            </span>
          )}
        </div>
      ),
      status: getStatusBadge(task.status),
      assigned_date: task.assigned_at ? new Date(task.assigned_at).toLocaleDateString() : '-',
    }))
  }, [conteosData, getStatusBadge, getOriginBadge, i18n])

  // Review columns
  const reviewColumns = useMemo(
    () => [
      { Header: i18n.t(`${T_PREFIX}.reviews.reviewer`), accessor: 'reviewer_name' },
      { Header: i18n.t(`${T_PREFIX}.reviews.location`), accessor: 'location_code' },
      { Header: i18n.t(`${T_PREFIX}.reviews.counter`), accessor: 'counter_name' },
      { Header: i18n.t(`${T_PREFIX}.reviews.divergent_products`), accessor: 'divergent_products' },
      { Header: i18n.t(`${T_PREFIX}.reviews.status`), accessor: 'status_badge' },
      { Header: i18n.t(`${T_PREFIX}.reviews.attempts`), accessor: 'review_attempts' },
      { Header: i18n.t(`${T_PREFIX}.reviews.submitted_at`), accessor: 'submitted_at_cell' },
    ],
    [i18n],
  )

  // Review table data
  const reviewTableData = useMemo(
    () =>
      reviewRows.map(row => ({
        object: row,
        // A group whose reviewer row is gone still carries the id — never fall through to "unassigned".
        reviewer_name: row.reviewer ? (
          row.reviewer.full_name || `#${row.reviewer.id}`
        ) : (
          <span className="text-red-700 font-medium">{i18n.t(`${T_PREFIX}.reviews.unassigned_group`)}</span>
        ),
        location_code: row.location_code,
        counter_name: row.counter?.full_name || '-',
        divergent_products: row.divergent_products,
        status_badge: getReviewStatusBadge(row.status),
        review_attempts: row.review_attempts,
        submitted_at_cell: row.submitted_at ? <FormattedRelativeTime date={row.submitted_at} className="text-gray-600" /> : '-',
      })),
    [reviewRows, getReviewStatusBadge, i18n],
  )

  // Location filter handler (BasicTable pattern — same as inventory page)
  const onLocationFilterChange = activeFilters => {
    locationColumns.forEach(col => {
      if (locationParams[col.accessor]) {
        delete locationParams[col.accessor]
      }
    })
    const filters = []
    activeFilters.forEach(filter => {
      filters[filter.id] = filter.value
    })
    tableRef.current?.resetPagination()
    const mergedFilters = { ...locationParams, ...filters }
    setLocationParams({ ...mergedFilters, page: 1, countable_only: true })
  }

  // Conteos filter config
  const [conteosFiltersData, setConteosFiltersData] = useState([
    {
      key: 'operator_name',
      placeholder: `${T_PREFIX}.conteos.filters.operator`,
      label: `${T_PREFIX}.conteos.operator`,
      data: {
        visible: false,
        type: 'text',
        value: '',
      },
    },
    {
      key: 'status',
      placeholder: `${T_PREFIX}.conteos.filters.status`,
      label: `${T_PREFIX}.conteos.status`,
      data: {
        visible: false,
        type: 'select',
        value: '',
        options: [
          { id: 'unassigned', label: `${T_PREFIX}.conteos.status_unassigned` },
          { id: 'pending', label: `${T_PREFIX}.conteos.status_pending` },
          { id: 'in_progress', label: `${T_PREFIX}.conteos.status_in_progress` },
          { id: 'in_review', label: `${T_PREFIX}.conteos.status_in_review` },
          { id: 'completed', label: `${T_PREFIX}.conteos.status_completed` },
        ],
      },
    },
    {
      key: 'origin',
      placeholder: `${T_PREFIX}.conteos.filters.origin`,
      label: `${T_PREFIX}.conteos.origin`,
      data: {
        visible: false,
        type: 'select',
        value: '',
        options: [
          { id: 'manual', label: `${T_PREFIX}.conteos.origin_manual` },
          { id: 'missing_stock', label: `${T_PREFIX}.conteos.origin_missing_stock` },
          { id: 'coverage_event', label: `${T_PREFIX}.conteos.origin_coverage_event` },
        ],
      },
    },
  ])

  // Conteos filter handler
  const onConteosSearchHandler = (updatedData, filterKey) => {
    // Destructive actions must not stay armed against rows the supervisor can no longer see.
    clearBatchSelection()
    setConteosFiltersData(prev => prev.map(item => (item.key === filterKey ? { ...item, data: updatedData } : item)))
    if (!updatedData.visible) {
      setConteosParams(prev => {
        const newParams = { ...prev, page: 1 }
        delete newParams[filterKey]
        return newParams
      })
    } else {
      setConteosParams(prev => ({ ...prev, [filterKey]: updatedData.value, page: 1 }))
    }
  }

  const handleResetConteosFilters = () => {
    // Destructive actions must not stay armed against rows the supervisor can no longer see.
    clearBatchSelection()
    setConteosParams({ page: 1, per_page: conteosParams.per_page, warehouse_id })
  }

  // Review filter config
  const [reviewFiltersData, setReviewFiltersData] = useState([
    {
      key: 'reviewer_name',
      placeholder: `${T_PREFIX}.reviews.filters.reviewer`,
      label: `${T_PREFIX}.reviews.reviewer`,
      data: { visible: false, type: 'text', value: '' },
    },
    {
      key: 'status',
      placeholder: `${T_PREFIX}.reviews.filters.status`,
      label: `${T_PREFIX}.reviews.status`,
      data: {
        visible: false,
        type: 'select',
        value: '',
        options: [
          { id: 'unassigned', label: `${T_PREFIX}.reviews.status_unassigned` },
          { id: 'pending', label: `${T_PREFIX}.reviews.status_pending` },
          { id: 'disagreement', label: `${T_PREFIX}.reviews.status_disagreement` },
        ],
      },
    },
  ])

  // Review filter handler
  const onReviewsSearchHandler = (updatedData, filterKey) => {
    clearReviewSelection()
    setResolvedSurplus(null)
    // The server ignores reviewer_name for the unassigned status (there's no reviewer yet),
    // so the UI shouldn't carry a stale value into a request that would silently drop it.
    // Both orderings matter: status flipping to unassigned, and reviewer_name being typed
    // while unassigned is already selected.
    const statusFilterValue = reviewFiltersData.find(item => item.key === 'status')?.data.value
    const clearingReviewerForUnassigned = filterKey === 'status' && updatedData.visible && updatedData.value === 'unassigned'
    const reviewerBlockedByUnassignedStatus = filterKey === 'reviewer_name' && statusFilterValue === 'unassigned'
    const effectiveData = reviewerBlockedByUnassignedStatus ? { ...updatedData, visible: false, value: '' } : updatedData
    setReviewFiltersData(prev =>
      prev.map(item => {
        if (item.key === filterKey) return { ...item, data: effectiveData }
        if (clearingReviewerForUnassigned && item.key === 'reviewer_name') return { ...item, data: { ...item.data, visible: false, value: '' } }
        return item
      }),
    )
    setReviewsParams(prev => {
      const next = { ...prev, page: 1 }
      if (effectiveData.visible) next[filterKey] = effectiveData.value
      else delete next[filterKey]
      if (clearingReviewerForUnassigned) delete next.reviewer_name
      return next
    })
  }

  const handleResetReviewFilters = () => {
    clearReviewSelection()
    setResolvedSurplus(null)
    setReviewsParams({ page: 1, per_page: reviewsParams.per_page, warehouse_id })
  }

  // Row click for conteos
  const onConteoRowClick = rowData => {
    setSelectedTask(rowData.object || rowData)
    setTaskPanelOpen(true)
  }

  return (
    <>
      <Notification show={notification.show} setShow={show => setNotification({ ...notification, show })} title={notification.title} type={notification.type} />

      <AssignCycleCountDialog
        open={assignDialogOpen}
        warehouseId={warehouse_id}
        selectedLocations={selectedLocations}
        onSuccess={onAssignSuccess}
        onCancel={() => setAssignDialogOpen(false)}
      />

      <BulkAssignCycleCountDialog open={bulkAssignOpen} warehouseId={warehouse_id} onCancel={() => setBulkAssignOpen(false)} onSuccess={onBulkAssignSuccess} />

      <UnassignCycleCountDialog
        open={unassignDialogOpen}
        warehouseId={warehouse_id}
        selectedBatches={selectedBatches}
        resolveCycleCountIds={unassignedBatchSelected ? resolveUnassignedPoolIds : undefined}
        onCancel={() => setUnassignDialogOpen(false)}
        onSuccess={onUnassignSuccess}
      />

      <ReassignCycleCountDialog
        open={reassignDialogOpen}
        warehouseId={warehouse_id}
        selectedBatches={selectedBatches}
        resolveCycleCountIds={unassignedBatchSelected ? resolveUnassignedPoolIds : undefined}
        onCancel={() => setReassignDialogOpen(false)}
        onSuccess={onReassignSuccess}
      />

      <ReassignCycleCountDialog
        open={reviewReassignOpen}
        warehouseId={warehouse_id}
        selectedBatches={toBatchesForReassign(selectedReviewRows)}
        onCancel={() => setReviewReassignOpen(false)}
        onSuccess={onReviewReassignSuccess}
      />

      <ResolveReviewDialog open={resolveOpen} location={selectedReviewRows[0]?._location} onCancel={() => setResolveOpen(false)} onSuccess={onResolveSuccess} />

      <UnassignCycleCountDialog
        open={reviewUnassignOpen}
        warehouseId={warehouse_id}
        selectedBatches={toBatchesForReassign(selectedReviewRows)}
        onCancel={() => setReviewUnassignOpen(false)}
        onSuccess={onReviewUnassignSuccess}
      />

      {taskPanelOpen && (
        <SlidePanel
          title={i18n.t(`${T_PREFIX}.task_panel.title`)}
          referrer={`/warehouse/${warehouse_id}/cycle-counts`}
          onClose={() => {
            setTaskPanelOpen(false)
            setSelectedTask(null)
          }}
          open={taskPanelOpen}
        >
          {selectedTask && (
            <CycleCountDetailPanel
              task={selectedTask}
              warehouseId={warehouse_id}
              onOpenEvent={id => {
                setTaskPanelOpen(false)
                setSelectedTask(null)
                setActiveTab('events')
                setEventsOpenId(id)
              }}
              onActionSuccess={(type, result) => {
                setTaskPanelOpen(false)
                setSelectedTask(null)
                refetchLocations()
                refetchConteos()
                refetchReviews()
                const key = type === 'unassign' ? 'unassign_modal' : 'reassign_modal'
                const count = type === 'unassign' ? result?.unassigned_count : result?.reassigned_count
                showNotification('success', i18n.t(`${T_PREFIX}.${key}.success`, { count: count ?? 0 }))
              }}
            />
          )}
        </SlidePanel>
      )}

      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">
              {i18n.t(`${T_PREFIX}.title`)} {` `}
              <span className="text-sm text-gray-400">
                {i18n.t(`${T_PREFIX}.warehouse`)} {warehouse_id}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'locations' && (
                <>
                  <ButtonLoader
                    className="whitespace-nowrap"
                    type="primary"
                    disabled={selectedLocations.length === 0}
                    onClick={() => setAssignDialogOpen(true)}
                  >
                    <PlusIcon className="mr-1 -ml-1 h-4 w-4" aria-hidden="true" />
                    {i18n.t(`${T_PREFIX}.assign_button`)}
                    {selectedLocations.length > 0 ? ` (${selectedLocations.length})` : ''}
                  </ButtonLoader>
                  <ButtonLoader className="whitespace-nowrap" type="primary" onClick={() => setBulkAssignOpen(true)}>
                    {i18n.t(`${T_PREFIX}.bulk_assign_button`)}
                  </ButtonLoader>
                </>
              )}
              {activeTab === 'conteos' && (
                <>
                  <ButtonLoader
                    className="whitespace-nowrap"
                    type="secondary"
                    disabled={selectedBatches.length === 0 || mixedPoolSelection}
                    onClick={() => setUnassignDialogOpen(true)}
                  >
                    {i18n.t(`${T_PREFIX}.unassign_button`)}
                    {selectedBatches.length > 0 ? ` (${selectedBatches.length})` : ''}
                  </ButtonLoader>
                  <ButtonLoader
                    className="whitespace-nowrap"
                    type="primary"
                    disabled={selectedBatches.length === 0 || mixedPoolSelection}
                    onClick={() => setReassignDialogOpen(true)}
                  >
                    {i18n.t(`${T_PREFIX}.reassign_button`)}
                    {selectedBatches.length > 0 ? ` (${selectedBatches.length})` : ''}
                  </ButtonLoader>
                </>
              )}
              {activeTab === 'reviews' && (
                <>
                  <ButtonLoader className="whitespace-nowrap" type="secondary" disabled={!canDiscardReview} onClick={() => setReviewUnassignOpen(true)}>
                    {i18n.t(`${T_PREFIX}.reviews.discard_button`)}
                    {canDiscardReview ? ` (${selectedReviewRows.length})` : ''}
                  </ButtonLoader>
                  <ButtonLoader className="whitespace-nowrap" type="secondary" disabled={!canResolve} onClick={() => setResolveOpen(true)}>
                    {i18n.t(`${T_PREFIX}.reviews.resolve_button`)}
                  </ButtonLoader>
                  <ButtonLoader className="whitespace-nowrap" type="primary" disabled={!canAssignReview} onClick={() => setReviewReassignOpen(true)}>
                    {i18n.t(`${T_PREFIX}.reviews.${reviewSelectionStatus === 'submitted' ? 'assign_button' : 'reassign_button'}`)}
                    {selectedReviewRows.length > 0 ? ` (${selectedReviewRows.length})` : ''}
                  </ButtonLoader>
                </>
              )}
            </div>
          </PageTopBar>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab} centered>
          <TabPane tab={i18n.t(`${T_PREFIX}.tabs.locations`)} key="locations">
            {!locationsPreviousData && locationsLoading && <Loader show={true} />}
            <BasicTable
              ref={tableRef}
              selectable
              onSelectionChange={rows => setSelectedLocations(rows)}
              disableCheck={row => !!row.original?._disabled}
              onFilterChange={onLocationFilterChange}
              columns={locationColumns}
              data={locationTableData}
              paginationMeta={locationsData?.meta}
              labelToPaginator={i18n.t(`${T_PREFIX}.locations.title`)}
              pagesSize={locationParams.per_page}
              onPaginationChange={page => setLocationParams({ ...locationParams, page })}
              onPageSizeChange={size => {
                setLocationParams({ ...locationParams, per_page: size, page: 1 })
                dispatch(setCycleCountsPageSize(size))
                tableRef.current?.resetPagination()
              }}
              showPaginator
              showHeader
              filterable
              queryOnClick
              showLoader={locationsFetching && locationsPreviousData}
              isFetching={locationsFetching}
              emptyTableText={i18n.t(`${T_PREFIX}.locations.empty_table`)}
            />
          </TabPane>
          <TabPane tab={i18n.t(`${T_PREFIX}.tabs.conteos`)} key="conteos">
            <div className="relative">
              <Loader show={conteosFetching && conteosPreviousData} />
              {!conteosPreviousData && conteosLoading ? (
                <Loader show={true} />
              ) : (
                <>
                  {conteosError && <p className="text-sm text-red-700 mb-2">{i18n.t(`${T_PREFIX}.conteos.load_error`)}</p>}
                  {mixedPoolSelection && <p className="text-sm text-red-600 mb-2">{i18n.t(`${EVENTS_T_PREFIX}.pool.mixed_selection`)}</p>}
                  {conteosTruncatedBatches.map(batch => (
                    <p key={batch.id} className="text-xs text-gray-500 mb-2">
                      <span className="font-medium">{batch.label}:</span> {i18n.t(`${EVENTS_T_PREFIX}.pool.capped`, { shown: batch.shown, total: batch.total })}
                    </p>
                  ))}
                  <NewTable
                    columns={conteosColumns}
                    // Keep the table (and its filter bar) mounted even on a load
                    // error — an old backend rejecting a filter (e.g. in_review)
                    // must not strand the user without the reset control needed
                    // to clear it.
                    data={conteosError ? [] : conteosTableData}
                    selectable
                    disableCheck={row => !!row.original?._disabled}
                    onSelectionChange={rows => setSelectedBatches(rows)}
                    deselectOrders={deselectBatches}
                    setDeselectOrders={setDeselectBatches}
                    filtersData={conteosFiltersData}
                    onSearchHandler={onConteosSearchHandler}
                    handleResetFilters={handleResetConteosFilters}
                    paginationMeta={conteosData?.meta}
                    labelToPaginator={i18n.t(`${T_PREFIX}.tabs.conteos`)}
                    pagesSize={conteosParams.per_page}
                    onPaginationChange={page => setConteosParams({ ...conteosParams, page })}
                    onPageSizeChange={size => {
                      // NewTable's page-size SelectInput hands back the whole
                      // { id, name } option, not the raw value — unwrap and
                      // coerce so `per_page` is never a Hash on the wire.
                      const perPage = Number(size?.id ?? size)
                      setConteosParams({ ...conteosParams, per_page: perPage, page: 1 })
                      dispatch(setCycleCountsPageSize(perPage))
                    }}
                    showPaginator
                    showHeader
                    filterable
                    // The red banner above already carries the load-error copy — an
                    // empty-table row under it would repeat the message and confuse
                    // "no results" with "couldn't load results".
                    emptyTableText={conteosError ? null : i18n.t(`${T_PREFIX}.conteos.empty_table`)}
                    cursorPointer
                    rowProps={row => ({
                      onClick: () => onConteoRowClick(row.original),
                    })}
                  />
                </>
              )}
            </div>
          </TabPane>
          <TabPane tab={i18n.t(`${T_PREFIX}.tabs.reviews`)} key="reviews">
            <div className="relative">
              <Loader show={reviewsFetching && reviewsPreviousData} />
              {!reviewsPreviousData && reviewsLoading ? (
                <Loader show={true} />
              ) : (
                <>
                  {reviewSelectionStatus === 'mixed' && !canDiscardReview && (
                    <p className="text-sm text-red-600 text-center mb-2">{i18n.t(`${T_PREFIX}.reviews.mixed_status_error`)}</p>
                  )}
                  {resolvedSurplus && (
                    <div className="rounded-md bg-indigo-50 border border-indigo-200 p-3 mb-2 text-sm text-indigo-900" role="status">
                      {resolvedSurplus.unitsCreated > 0 && (
                        <span>
                          {i18n.t(`${T_PREFIX}.found_stock.declared_banner`, {
                            count: resolvedSurplus.unitsCreated,
                            location: resolvedSurplus.location_code,
                          })}{' '}
                        </span>
                      )}
                      {resolvedSurplus.rowsSkipped > 0 && (
                        <span>
                          {i18n.t(`${T_PREFIX}.found_stock.skipped_banner`, {
                            count: resolvedSurplus.rowsSkipped,
                            reasons: resolvedSurplus.reasons.map(r => `${reasonLabel(r.key)} (${r.count})`).join(', '),
                          })}{' '}
                        </span>
                      )}
                      {resolvedSurplus.unitsCreated > 0 && (
                        <Link className="font-medium underline" to={foundStockDeepLink(warehouse_id, resolvedSurplus)}>
                          {i18n.t(`${T_PREFIX}.found_stock.view_link`)}
                        </Link>
                      )}
                    </div>
                  )}
                  {reviewTruncatedGroups.map(group => (
                    <p key={group.id} className="text-xs text-amber-700 mb-2">
                      {i18n.t(`${T_PREFIX}.reviews.truncated_notice`, {
                        shown: group.shown,
                        total: group.total_locations,
                        reviewer: group.reviewer ? group.reviewer.full_name || `#${group.reviewer.id}` : i18n.t(`${T_PREFIX}.reviews.unassigned_group`),
                      })}
                    </p>
                  ))}
                  <NewTable
                    columns={reviewColumns}
                    // Keep the table (and its filter bar) mounted even on a
                    // load error — see the same comment on the Conteos tab.
                    data={reviewsError ? [] : reviewTableData}
                    selectable
                    onSelectionChange={rows => setSelectedReviewRows(rows.map(r => r.object || r))}
                    deselectOrders={deselectReviews}
                    setDeselectOrders={setDeselectReviews}
                    filtersData={reviewFiltersData}
                    onSearchHandler={onReviewsSearchHandler}
                    handleResetFilters={handleResetReviewFilters}
                    paginationMeta={reviewsData?.meta}
                    labelToPaginator={i18n.t(`${T_PREFIX}.tabs.reviews`)}
                    pagesSize={reviewsParams.per_page}
                    onPaginationChange={page => setReviewsParams({ ...reviewsParams, page })}
                    onPageSizeChange={size => {
                      // Same unwrap as the Conteos tab: NewTable passes the
                      // SelectInput's { id, name } option through untouched.
                      const perPage = Number(size?.id ?? size)
                      setReviewsParams({ ...reviewsParams, per_page: perPage, page: 1 })
                      dispatch(setCycleCountsPageSize(perPage))
                    }}
                    showPaginator
                    showHeader
                    filterable
                    emptyTableText={i18n.t(reviewsError ? `${T_PREFIX}.reviews.load_error` : `${T_PREFIX}.reviews.empty_table`)}
                  />
                </>
              )}
            </div>
          </TabPane>
          <TabPane tab={i18n.t(`${T_PREFIX}.events.tab`)} key="events">
            <CoverageEventsTab
              key={warehouse_id}
              warehouseId={warehouse_id}
              active={activeTab === 'events'}
              openEventId={eventsOpenId}
              onOpenEventHandled={() => setEventsOpenId(null)}
              onCountsChanged={() => {
                refetchLocations()
                refetchConteos()
                refetchReviews()
              }}
            />
          </TabPane>
        </Tabs>
      </PageView>
    </>
  )
}
